import { useTrack } from "../lib/analytics";
import { UPDATED } from "../lib/legal";
// The registry, never the barrel: the barrel imports all nine translations and
// this component renders in the browser (see lib/marketing/registry.ts).
import {
  type ContentLang,
  langPath,
  type MarketingBundle,
  SITE_LANG_ENDONYM,
  SITE_LANGS,
} from "../lib/marketing/registry";
import { appStoreLink } from "../lib/stores";

/**
 * The landing page, in one of the nine languages the app speaks.
 *
 * Every string comes from the bundle — see `lib/marketing/types.ts` for why the
 * page is data. The only literals here are the brand name, which is the same in
 * all nine, and the two screenshot paths.
 *
 * ── Responsive rules this file follows ───────────────────────────────────────
 *
 * Mobile first, and that is not a slogan here: this is an app for a phone, most
 * of its traffic arrives from a phone, and every visitor who taps a store button
 * is on one. So the single-column layout is the DEFAULT and the two-column
 * arrangements are `lg:` additions on top of it.
 *
 * Headings size with `clamp()` rather than with breakpoints. A step at 640px
 * means a headline is one size at 639px and another at 641px, and the 360px
 * phones — still a third of Android — get whatever the smallest step happened to
 * be. `clamp()` gives every width its own size and has no bad case in between.
 *
 * Every `<img>` carries its intrinsic `width` and `height`. Without them the
 * browser reserves no space, the page reflows when each screenshot arrives, and
 * that reflow is measured directly as Cumulative Layout Shift — one of the three
 * Core Web Vitals the search ranking reads.
 */

/** `"August 16, 2026"` → `"2026"`. See the footer for why it is not the clock. */
const COPYRIGHT_YEAR = UPDATED.slice(-4);

/** Section shell: the one place the page's horizontal rhythm is defined. */
function Section({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`px-5 py-14 sm:px-6 sm:py-20 ${className}`}>
      <div className="mx-auto w-full max-w-5xl">{children}</div>
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
      {children}
    </p>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-[clamp(1.75rem,5vw,2.5rem)] leading-[1.2] font-bold tracking-tight text-ink">
      {children}
    </h2>
  );
}

/**
 * The store button, and the Android badge beside it.
 *
 * `from` names the section that produced the tap, so the funnel can tell a hero
 * click from a bottom-of-page click — which is the difference between copy that
 * sold the app and copy that merely failed to stop someone.
 *
 * Only the App Store half is a link. The Play listing does not exist yet, and a
 * button that 404s costs more than the one it replaces — see `lib/stores.ts`.
 */
export function StoreButtons({
  bundle,
  from,
}: {
  bundle: MarketingBundle;
  from: string;
}) {
  const track = useTrack();
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <a
        href={appStoreLink(from)}
        onClick={() => track("web_store_click", { store: "appstore", from })}
        className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
      >
        {bundle.cta.appStore}
      </a>
      {/* Android is still being built, so this is a status and not a button:
          dashed border, inset fill, no rim — the three things that read as
          "not pressable" before the words are. It is shown rather than hidden
          because an Android visitor who sees only an App Store button learns
          nothing and leaves, while "coming soon" answers what they came with. */}
      <span
        aria-disabled="true"
        className="inline-flex h-[54px] items-center justify-center rounded-pill border-[2.5px] border-dashed border-ink2 bg-inset px-7 font-display text-[17px] font-bold text-body"
      >
        {bundle.cta.androidSoon}
      </span>
    </div>
  );
}

/**
 * A phone screenshot in its own ink frame.
 *
 * `loading` is a parameter rather than always `lazy`: the hero shot is inside
 * the first viewport on a phone, and lazy-loading the largest element above the
 * fold delays the very paint that Largest Contentful Paint is timing.
 */
function Shot({
  lang,
  file,
  alt,
  sizes,
  priority = false,
}: {
  lang: ContentLang;
  file: string;
  alt: string;
  /** CSS width of the slot at each breakpoint — see the note below. */
  sizes: string;
  priority?: boolean;
}) {
  return (
    <img
      src={`/shots/${lang}/${file}.webp`}
      // Two widths, and `sizes` is what makes them worth having.
      //
      // Lighthouse measured 123 KiB wasted here: a 640px-wide file decoded into
      // a 234px slot on desktop, four times over. The browser cannot know the
      // slot is 234px until layout, which is far too late to choose a file — so
      // `sizes` tells it in advance, and it picks the smaller variant for the
      // strip while still taking the 640 for a hero on a 2× phone.
      //
      // `-320` and not `@320`: an `@` in a path is legal and Cloudflare answers
      // it with a 307 to the percent-encoded form, so every 2× visitor paid a
      // redirect before the image. Measured, then renamed.
      srcSet={`/shots/${lang}/${file}-320.webp 320w, /shots/${lang}/${file}.webp 640w`}
      sizes={sizes}
      alt={alt}
      width={640}
      height={1392}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      // `fetchPriority` only matters for the one image that is the LCP element;
      // on every other it is noise that competes with it.
      fetchPriority={priority ? "high" : undefined}
      className="h-auto w-full rounded-[28px] border-[2.5px] border-ink bg-surface shadow-[5px_5px_0_0_var(--color-ink)]"
    />
  );
}

/**
 * The language switcher — a `<details>`, not a `<select>` and not a menu.
 *
 * It has to work with JavaScript turned off, because the thing it switches is
 * which language the page is in and a visitor who cannot read the current one
 * has no way to ask for help. `<details>` is the only disclosure widget the
 * browser opens on its own, and the nine children are real `<a href>`s, so this
 * is also how a crawler walks from one translation to the next.
 */
function LangMenu({ current, label }: { current: ContentLang; label: string }) {
  return (
    <details className="relative">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-pill border-[2.5px] border-ink bg-surface px-3.5 py-1.5 font-display text-sm font-bold text-ink [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true">🌐</span>
        <span>{SITE_LANG_ENDONYM[current]}</span>
      </summary>
      <nav
        aria-label={label}
        className="absolute right-0 z-30 mt-2 flex w-44 flex-col gap-0.5 rounded-[20px] border-[2.5px] border-ink bg-surface p-2 shadow-[5px_5px_0_0_var(--color-ink)]"
      >
        {SITE_LANGS.map((code) => (
          <a
            key={code}
            href={langPath(code)}
            hrefLang={code}
            aria-current={code === current ? "page" : undefined}
            className={
              code === current
                ? "rounded-[14px] bg-brand-wash px-3 py-2 text-sm font-bold text-ink"
                : "rounded-[14px] px-3 py-2 text-sm text-body transition-colors hover:bg-inset hover:text-brand"
            }
          >
            {SITE_LANG_ENDONYM[code]}
          </a>
        ))}
      </nav>
    </details>
  );
}

export function Landing({
  bundle,
  lang,
}: {
  bundle: MarketingBundle;
  lang: ContentLang;
}) {
  // `?lang=` on the legal links, so a Korean visitor who taps "privacy" gets the
  // Korean policy. The landing page carries its language in the path; the legal
  // pages carry theirs in a query string, and this is the seam between the two.
  const legalQuery = `?lang=${lang}`;

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <a
        href="#content"
        className="sr-only rounded-pill border-[2.5px] border-ink bg-surface px-4 py-2 font-display text-sm font-bold text-ink focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50"
      >
        {bundle.ui.skipToContent}
      </a>

      <header className="sticky top-0 z-20 border-b-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-5 py-3.5 sm:px-6">
          <a
            href={langPath(lang)}
            className="font-display text-xl font-bold tracking-tight text-ink"
          >
            Bible Shield
          </a>
          <div className="flex items-center gap-2.5">
            <LangMenu current={lang} label={bundle.ui.languageLabel} />
            {/* One store button in the header, and only from `sm` up: on a
                phone the hero's own buttons are a thumb-length away, and a
                second copy would push the headline below the fold. */}
            <a
              href={appStoreLink("header")}
              className="rim hidden h-10 items-center justify-center bg-brand px-5 font-display text-sm font-bold text-onbrand sm:inline-flex"
            >
              {bundle.cta.appStore}
            </a>
          </div>
        </div>
      </header>

      <main id="content" className="flex-1">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <Section className="pt-10 sm:pt-16">
          {/* A FIXED width for the phone column, not a fraction. A phone
              screenshot is 1:2.17, so a fractional column makes the image as
              tall as the viewport on a wide screen — the hero then scrolls, and
              the headline and the store buttons stop sharing a screen. */}
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_300px] lg:gap-16">
            <div>
              <Eyebrow>{bundle.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 font-display text-[clamp(2.25rem,7vw,3.75rem)] leading-[1.1] font-bold tracking-tight text-ink text-balance">
                {bundle.hero.headline}
              </h1>
              <p className="mt-5 max-w-xl text-[clamp(1rem,2.2vw,1.175rem)] leading-relaxed text-body">
                {bundle.hero.sub}
              </p>
              <div className="mt-8">
                <StoreButtons bundle={bundle} from="hero" />
              </div>
              <p className="mt-4 text-sm text-ink2">{bundle.hero.note}</p>
            </div>
            <div className="mx-auto w-full max-w-[240px] sm:max-w-[280px]">
              <Shot
                lang={lang}
                file={bundle.shots.items[0].file}
                alt={bundle.shots.items[0].alt}
                sizes="(min-width: 1024px) 300px, (min-width: 640px) 280px, 240px"
                priority
              />
            </div>
          </div>
        </Section>

        {/* ── How it works ───────────────────────────────────────────────── */}
        <Section className="border-t-[2.5px] border-ink bg-inset">
          <SectionHeading>{bundle.steps.heading}</SectionHeading>
          <ol className="mt-8 grid gap-5 md:grid-cols-3">
            {bundle.steps.items.map((step, i) => (
              <li
                key={step.title}
                className="brutal flex flex-col gap-3 p-6 sm:p-7"
              >
                <span
                  aria-hidden="true"
                  className="inline-flex size-10 items-center justify-center rounded-pill border-[2.5px] border-ink bg-brand font-display text-lg font-bold text-onbrand"
                >
                  {i + 1}
                </span>
                <h3 className="font-display text-xl font-bold text-ink">
                  {step.title}
                </h3>
                <p className="leading-relaxed text-body">{step.body}</p>
              </li>
            ))}
          </ol>
        </Section>

        {/* ── Screenshots ────────────────────────────────────────────────── */}
        <Section className="border-t-[2.5px] border-ink">
          <SectionHeading>{bundle.shots.heading}</SectionHeading>
          {/* Two across on a phone rather than one: at one per row the strip is
              four full screens of scrolling and nobody reaches the FAQ. */}
          <ul className="mt-8 grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4">
            {bundle.shots.items.map((shot) => (
              <li key={shot.file} className="flex flex-col gap-3">
                <Shot
                  lang={lang}
                  file={shot.file}
                  alt={shot.alt}
                  sizes="(min-width: 1024px) 234px, 45vw"
                />
                <p className="text-sm leading-snug text-ink2">{shot.caption}</p>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Features ───────────────────────────────────────────────────── */}
        <Section className="border-t-[2.5px] border-ink bg-inset">
          <SectionHeading>{bundle.features.heading}</SectionHeading>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {bundle.features.items.map((feature) => (
              <li
                key={feature.title}
                className="brutal flex flex-col gap-2 p-6"
              >
                <h3 className="font-display text-lg font-bold text-ink">
                  {feature.title}
                </h3>
                <p className="leading-relaxed text-body">{feature.body}</p>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── FAQ ────────────────────────────────────────────────────────── */}
        <Section className="border-t-[2.5px] border-ink">
          <SectionHeading>{bundle.faq.heading}</SectionHeading>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            {bundle.faq.items.map((item) => (
              <details key={item.q} className="brutal p-5 sm:p-6">
                <summary className="cursor-pointer list-none font-display text-lg font-bold text-ink [&::-webkit-details-marker]:hidden">
                  {item.q}
                </summary>
                <p className="mt-3 leading-relaxed text-body">{item.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* ── Closing CTA ────────────────────────────────────────────────── */}
        <Section className="border-t-[2.5px] border-ink bg-brand">
          <div className="flex flex-col items-start gap-6">
            <h2 className="font-display text-[clamp(1.75rem,5vw,2.5rem)] leading-[1.2] font-bold tracking-tight text-onbrand text-balance">
              {bundle.closing.heading}
            </h2>
            <p className="max-w-xl text-lg leading-relaxed text-onbrand">
              {bundle.closing.body}
            </p>
            <StoreButtons bundle={bundle} from="closing" />
          </div>
        </Section>
      </main>

      <footer className="border-t-[2.5px] border-ink bg-surface">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-5 py-10 sm:px-6">
          <nav
            aria-label={bundle.ui.footerLabel}
            className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink2"
          >
            {/* The blog's only link from the home page, and the reason its
                articles are crawlable at all: a page reachable solely from the
                sitemap is one Google finds late and treats as unimportant.
                English label in all nine locales because the writing IS English
                — a translated word pointing at an English page is a worse
                promise than an untranslated one. */}
            <a
              href="/about"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              about
            </a>
            <a
              href="/blog"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              blog
            </a>
            <a
              href="/bible"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              books of the bible
            </a>
            <a
              href="/block"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              block an app
            </a>
            <a
              href="/prayers"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              prayers
            </a>
            <a
              href="/verse"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              passages
            </a>
            <a
              href="/verses"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              verses
            </a>
            <a
              href="/quiz"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              quizzes
            </a>
            <a
              href={`/privacy${legalQuery}`}
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              {bundle.ui.privacy}
            </a>
            <a
              href={`/terms${legalQuery}`}
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              {bundle.ui.terms}
            </a>
            <a
              href="/support"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              {bundle.ui.support}
            </a>
            <a
              href="/delete"
              className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              {bundle.ui.deleteAccount}
            </a>
          </nav>

          {/* The full language list, in the footer and not only in the header
              menu: it is a flat set of nine crawlable `hrefLang` links on every
              page, which is what makes the alternates in the <head> verifiable
              rather than merely asserted. */}
          <nav
            aria-label={bundle.ui.languageLabel}
            className="flex flex-wrap gap-x-4 gap-y-2 text-sm"
          >
            <span className="text-ink2">{bundle.ui.languageLabel}:</span>
            {SITE_LANGS.map((code) => (
              <a
                key={code}
                href={langPath(code)}
                hrefLang={code}
                aria-current={code === lang ? "page" : undefined}
                className={
                  code === lang
                    ? "font-bold text-ink"
                    : "text-ink2 underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
                }
              >
                {SITE_LANG_ENDONYM[code]}
              </a>
            ))}
          </nav>

          {/* The year comes from the legal constant rather than from the clock.
              `new Date()` here would be evaluated once in the Worker (UTC) and
              again in the browser (local time), which is a hydration mismatch
              for the several hours a year the two disagree about the date — and
              a wrong copyright year is not worth a React warning in production.
              This constant already moves whenever the legal text is republished. */}
          <p className="text-sm text-ink2">
            © {COPYRIGHT_YEAR} AKES Solutions Limited
          </p>
        </div>
      </footer>
    </div>
  );
}
